/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from "react-router-dom";
import { Breadcrumb, Col, Row, Table, Select } from 'antd'
import { CaretDownOutlined } from '@ant-design/icons';
import PageTitle from '../components/PageTitle';
import PageLayout from '../components/PageLayout';
import PageSearch from '../components/PageSearch';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import StampStatusObj from '../helper/StampStatusObj';
import { getLookupCodeList, queryUnclosed } from '../redux/action/Stamp';

const { Option } = Select

// 未結案會出現的狀態（非 done/cancel/reject/stampReject）
const statusList = [
  { label: '審核中', value: 'pendingAudit' },
  { label: '用印中', value: 'pendingStamp' },
  { label: '上傳中', value: 'pendingUpload' },
  { label: '結案中', value: 'pendingProcessed' },
]
const statusValues = statusList.map((s) => s.value)

export default function StampUnclosedTracking() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isLoading = useSelector((state) => state.globalSetting.isLoading)
  const stampList = useSelector((state) => state.Stamp.stampList)
  const [list, setList] = useState([])
  const [searchFilter, setSearchFilter] = useState(null)
  const [pageSize, setPageSize] = useState(10)
  const [pageNow, setPageNow] = useState(1)

  useEffect(() => {
    localStorage.setItem('currentMenuItemsKey', 'StampUnclosedTracking')
    if (!stampList?.length) {
      dispatch(getLookupCodeList('stampType', ''))
    }
    dispatch(queryUnclosed()).then((resp) => {
      setList(Array.isArray(resp) ? resp : [])
    })
  }, [dispatch])

  // 印信代碼 → 中文描述
  const stampMap = useMemo(() => {
    const map = {}
      ; (stampList || []).forEach((item) => { map[item.lookup_code] = item.description })
    return map
  }, [stampList])

  const renderStampType = (record) =>
    (record.stamp_type || []).map((code) => stampMap[code] || code).join('、')

  // 目前關卡：sentinel 'stampOwner' 顯示中文「印信保管人」
  const renderAuditor = (record) => {
    const name = record.next_auditor_name
    if (!name || name === 'stampOwner') return '印信保管人'
    return name
  }

  const filtered = (list || []).filter((r) => {
    const f = searchFilter
    if (!f) return true
    if (f.stamp_type && f.stamp_type.length && !f.stamp_type.includes(r.stamp_status)) return false
    if (f.stamper_type && f.stamper_type.length && !(r.stamp_type || []).some((c) => f.stamper_type.includes(c))) return false
    if (f.stamp_no && Number(r.stamp_no) !== Number(f.stamp_no)) return false
    if (f.document && !String(r.document || '').toLowerCase().includes(String(f.document).toLowerCase())) return false
    if (f.applicant_name && !String(r.applicant_name || '').toLowerCase().includes(String(f.applicant_name).toLowerCase())) return false
    if (f.start_date && Number(r.cr_date) < Number(f.start_date)) return false
    if (f.end_date && Number(r.cr_date) > Number(f.end_date)) return false
    return true
  })

  const onView = (record) => {
    localStorage.setItem('currentMenuItemsKey', 'StampUnclosedTracking')
    const opt = { state: { from: 'StampUnclosedTracking' } }
    // 用印中 → 用印審核頁(可轉派)；待上傳 → 待上傳檢視；其他 → 一般檢視
    if (record.stamp_status === 'pendingStamp') {
      navigate(`/Stamp/StampOwnerAudit/${record.stamp_no}`, opt)
    } else if (record.stamp_status === 'pendingUpload') {
      navigate(`/Stamp/StampApplyViewPendingUpload/${record.stamp_no}`, opt)
    } else {
      navigate(`/Stamp/StampApplyView/${record.stamp_no}`, opt)
    }
  }

  const columns = [
    {
      title: '功能',
      align: 'center',
      key: 'action',
      render: (_, record) => (
        <Row justify='space-evenly'>
          <a onClick={() => onView(record)}> 檢視 </a>
        </Row>
      )
    },
    { title: '編號', align: 'center', dataIndex: 'stamp_no', key: 'stamp_no' },
    { title: '申請人', align: 'center', dataIndex: 'applicant_name', key: 'applicant_name', width: '13%' },
    { title: '文件名稱', align: 'center', dataIndex: 'document', key: 'document', width: '18%' },
    { title: '印信', align: 'center', key: 'stamp_type', width: '14%', render: (_, record) => renderStampType(record) },
    {
      title: '目前狀態',
      align: 'center',
      dataIndex: 'stamp_status',
      key: 'stamp_status',
      render: (_, record) => StampStatusObj[record.stamp_status] || record.stamp_status
    },
    { title: '目前關卡', align: 'center', key: 'next_auditor_name', width: '13%', render: (_, record) => renderAuditor(record) },
    {
      title: '申請日期',
      align: 'center',
      dataIndex: 'cr_date',
      key: 'cr_date',
      width: '11%',
      render: (_, record) => record.cr_date ? moment.unix(record.cr_date / 1000).format('YYYY/MM/DD') : ''
    },
  ]

  return (
    <PageLayout spinning={isLoading !== 0}>
      <Col span={24}>
        <Breadcrumb separator=">">
          <Breadcrumb.Item href='/'>WiAdvance EIP</Breadcrumb.Item>
          <Breadcrumb.Item href='/Stamp/StampApplication'>用印申請</Breadcrumb.Item>
          <Breadcrumb.Item>未結案追蹤</Breadcrumb.Item>
        </Breadcrumb>
      </Col>
      <PageTitle>未結案追蹤</PageTitle>
      <PageSearch
        statusList={statusList}
        defaultSearchParams={statusValues}
        nextAuitor={null}
        onSearchParamsChange={(params) => { setSearchFilter(params); setPageNow(1) }}
      />
      <Col span={24} style={{ margin: '10px 0px' }} />
      <Col span={24}>
        <Table
          pagination={{ position: ['topRight', 'bottomRight'], pageSize, current: pageNow }}
          dataSource={filtered}
          rowKey={(record) => record.stamp_no}
          columns={columns}
          onChange={(page) => { setPageNow(page.current); setPageSize(page.pageSize) }}
          footer={() => (
            <Col flex="auto">
              <div style={{ textAlign: 'end', margin: '8px', color: '#7C7C7C' }}>
                共有{filtered.length}筆資料，每頁顯示
                <Select
                  value={pageSize}
                  style={{ width: pageSize < 100 ? 60 : 70, color: '#a5a5a5' }}
                  bordered={false}
                  suffixIcon={<CaretDownOutlined />}
                  onChange={(e) => { setPageSize(e); setPageNow(1) }}
                >
                  <Option value={5}>5</Option>
                  <Option value={10}>10</Option>
                  <Option value={25}>25</Option>
                  <Option value={50}>50</Option>
                  <Option value={100}>100</Option>
                </Select>
                筆
              </div>
            </Col>
          )}
        />
      </Col>
    </PageLayout>
  )
}
